import React, { useState, useEffect } from "react";
import { getSharedList, checkGroupItems, clearCheckedShared, deleteItem } from "../api";
import CategoryGroup from "./CategoryGroup";

/**
 * Liste commune — articles agrégés de tous les membres.
 * Lecture seule pour les utilisateurs normaux, mutations pour l'admin.
 */
function SharedList({ onRefresh, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const fetchItems = async (pageNum = 1, append = false) => {
    try {
      const data = await getSharedList(pageNum);
      if (append) {
        setItems((prev) => [...prev, ...data.items]);
      } else {
        setItems(data.items || []);
      }
      setTotalItems(data.total || 0);
      setHasMore(pageNum < (data.pages || 1));
      setPage(pageNum);
    } catch (err) {
      console.error("Erreur chargement liste commune:", err);
      setItems([]);
      setTotalItems(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleLoadMore = () => {
    fetchItems(page + 1, true);
  };

  // Cocher/décocher un groupe (admin uniquement)
  const handleCheck = async (item) => {
    try {
      await checkGroupItems(item.ids);
      fetchItems();
      onRefresh?.();
    } catch (err) {
      console.error("Erreur mise à jour:", err);
    }
  };

  // Supprimer les articles du user courant dans un groupe (admin uniquement)
  const handleDelete = async (itemOrId) => {
    const myIds = itemOrId && typeof itemOrId === "object" ? itemOrId.my_ids : null;

    if (!myIds || myIds.length === 0) {
      window.alert("Cet article appartient à d'autres membres.");
      return;
    }

    const itemName = typeof itemOrId === "object" ? itemOrId.name : "";
    if (!window.confirm(`Supprimer "${itemName}" de la liste ?`)) return;

    try {
      await Promise.all(myIds.map((id) => deleteItem(id)));
      fetchItems();
      onRefresh?.();
    } catch (err) {
      console.error("Erreur suppression:", err);
    }
  };

  // Vider tous les articles cochés (admin uniquement)
  const handleClearChecked = async () => {
    const checkedCount = items.filter((i) => i.checked).length;
    if (!window.confirm(`Supprimer les ${checkedCount} articles cochés ?`)) return;
    try {
      await clearCheckedShared();
      fetchItems();
      onRefresh?.();
    } catch (err) {
      console.error("Erreur suppression:", err);
    }
  };

  if (loading) return <p className="loading-text">Chargement...</p>;

  const unchecked = items.filter((i) => !i.checked);
  const checked = items.filter((i) => i.checked);

  const grouped = {};
  unchecked.forEach((item) => {
    const cat = item.category || "Autre";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  });

  if (items.length === 0) {
    return (
      <div className="empty-state">
        <span className="empty-icon">&#x1F4CB;</span>
        <p>Aucun article dans la liste commune</p>
        <p className="empty-hint">Les articles ajoutés par chaque membre apparaissent ici</p>
      </div>
    );
  }

  return (
    <div className="item-list">
      {!isAdmin && (
        <div className="readonly-banner">
          Lecture seule — seul l'admin peut cocher et supprimer
        </div>
      )}

      {Object.entries(grouped).map(([category, catItems]) => (
        <CategoryGroup
          key={category}
          category={category}
          items={catItems}
          onCheck={isAdmin ? handleCheck : undefined}
          onDelete={isAdmin ? handleDelete : undefined}
          showOwner={true}
          canCheck={isAdmin}
          canDelete={isAdmin}
        />
      ))}

      {checked.length > 0 && (
        <details className="checked-section">
          <summary className="checked-header">
            &#x2705; Terminé ({checked.length})
            {isAdmin && (
              <button
                className="btn-clear-checked"
                onClick={(e) => { e.preventDefault(); handleClearChecked(); }}
                title="Supprimer tous les articles cochés"
              >
                &#x1F5D1; Tout vider
              </button>
            )}
          </summary>
          <ul className="category-items">
            {checked.map((item) => (
              <li key={(item.ids || []).join("-") || item.id} className="shopping-item checked">
                {isAdmin ? (
                  <button className="item-checkbox" onClick={() => handleCheck(item)}>
                    &#x2705;
                  </button>
                ) : (
                  <span className="item-status-icon" aria-hidden="true">&#x2705;</span>
                )}
                <div className="item-info">
                  <span className="item-name">{item.name}</span>
                </div>
                {item.users && (
                  <div className="item-owners">
                    {item.users.map((u) => (
                      <span
                        key={u.id}
                        className="item-owner"
                        style={{ backgroundColor: `${u.color}25`, color: u.color }}
                        title={u.username}
                      >
                        {u.emoji}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {hasMore && (
        <button className="btn-load-more" onClick={handleLoadMore}>
          Charger plus ({items.length}/{totalItems})
        </button>
      )}
    </div>
  );
}

export default SharedList;
